import { from, Observable, of } from 'rxjs';
import { catchError, mergeMap, withLatestFrom } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import Pusher from 'pusher-js';
import { forEach } from 'lodash';
import {
  ChatAction,
  ChatActions,
  LoadMessages, LoadMessagesFailed, LoadMessagesSuccess,
  MessageReceived,
  PusherConnect,
  PusherConnectFailed,
  PusherConnectSuccess,
  PusherDisconnect, SendMessage, SendMessageFailed, SendMessageSuccess,
  SubscribeChannel, UnsubscribeChannel,
} from '../actions/chat.action';
import { ChatMessage } from '../../shared/models/chat';
import ChatService from '../services/chat.service';
import notificationSound from '../../assets/files/glympse-notification-sound.m4a';
import { getItem } from '../../shared/utils/local-storage-utils';
import { CHAT_SOUND_NOTIFICATIONS, GLYMPSE_PRO_CHAT_ORIGIN } from '../../shared/constants/chat.constants';
import { RootState } from '../../store/state';


// eslint-disable-next-line import/no-mutable-exports
export let pusherClient: Pusher;
const SOUND_NOTIFICATION = new Audio(notificationSound);

const pusherConnectEpic = (action$: Observable<ChatAction>) => action$.pipe(
  ofType<PusherConnect>(ChatActions.PusherConnect),
  mergeMap((action) => new Observable((observer) => {
    pusherClient = new Pusher(action.payload.key, action.payload.options);

    pusherClient.connection.bind('connected', (result: any) => {
      observer.next(new PusherConnectSuccess(result));
    });

    pusherClient.connection.bind('error', (error: any) => {
      observer.next(new PusherConnectFailed(error));
    });
  })),
);

const pusherDisconnectEpic = (action$: Observable<ChatAction>, state$: Observable<RootState>) => action$.pipe(
  ofType<PusherDisconnect>(ChatActions.PusherDisconnect),
  withLatestFrom(state$),
  mergeMap(([, state]) => new Observable((observer) => {
    forEach(state?.chat?.chats, (chat) => {
      observer.next(new UnsubscribeChannel(chat?.chat_id));
    });
    pusherClient?.disconnect();
  })),
);

const subscribeToChannelEpic = (action$: Observable<ChatAction>) => action$.pipe(
  ofType<SubscribeChannel>(ChatActions.SubscribeChannel),
  mergeMap((action) => new Observable((observer) => {
    if (!action?.payload?.chatInfo?.completed) {
      const channel = pusherClient.subscribe(`${action.payload.chat_channel}`)
      channel.bind('NEW_MESSAGE', (message: ChatMessage) => {
        observer.next(new MessageReceived({
          chat_channel: action.payload.chat_channel,
          message
        }));
        const chatNotifications = getItem(CHAT_SOUND_NOTIFICATIONS);
        if (chatNotifications && chatNotifications === 'true' && message?.origin !== GLYMPSE_PRO_CHAT_ORIGIN) {
          SOUND_NOTIFICATION.play();
        }
      });
    }
  })),
);

const unsubscribeChannelEpic = (action$: Observable<ChatAction>) => action$.pipe(
  ofType<UnsubscribeChannel>(ChatActions.UnsubscribeChannel),
  mergeMap((action) => new Observable(() => {
    pusherClient?.unsubscribe(`${action.payload}`)
  })),
);

const loadMessagesEpic = (action$: Observable<ChatAction>) => action$.pipe(
  ofType<LoadMessages>(ChatActions.LoadMessages),
  mergeMap((action) => from(ChatService.loadMessages(action.payload)).pipe(
    mergeMap((response) => of(
      new LoadMessagesSuccess({
        chat_channel: action.payload,
        messages: response
      })
    )),
    catchError((error) => (
      of(new LoadMessagesFailed({
        error,
        chat_channel: action.payload,
      }))
    )),
  )),
);

const sendMessageEpic = (action$: Observable<ChatAction>) => action$.pipe(
  ofType<SendMessage>(ChatActions.SendMessage),
  mergeMap((action) => from(ChatService.sendMessage(action.payload.chat_channel, action.payload.message, action.payload.invite_code)).pipe(
    mergeMap((response) => of(
      new SendMessageSuccess({
        chat_channel: action.payload.chat_channel,
        message: response
      })
    )),
    catchError((error) => (
      of(new SendMessageFailed({
        error,
        chat_channel: action.payload.chat_channel,
      }))
    )),
  )),
);


const ChatEpics = [
  pusherConnectEpic,
  pusherDisconnectEpic,
  subscribeToChannelEpic,
  unsubscribeChannelEpic,
  loadMessagesEpic,
  sendMessageEpic
];

export default ChatEpics;
